import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDatepickerI18n,
  NgbDateStruct,
  NgbTimepicker,
  NgbTimeStruct,
} from '@ng-bootstrap/ng-bootstrap';
import { SbLocalDateTime } from 'src/app/services/api/api-date-time';
import { SbDateISOParserFormatter } from 'src/app/services/date-time/date-formatter-iso.service';
import { SbDateStructAdapter } from 'src/app/services/date-time/ngb-sb-date-struct-adapter.service';
import { DateTime } from 'luxon';
import { UtilService } from '../../../services/util.service';
import { TranslateService } from '@ngx-translate/core';
import { NgbTranslatedDatepicker } from '../../../services/date-time/ngb-datepicker-translated.service';

@Component({
  selector: 'sb-local-date-time-input',
  templateUrl: './sb-local-date-time-input.component.html',
  styleUrls: ['./sb-local-date-time-input.component.scss'],
  providers: [
    { provide: NgbDatepickerI18n, useClass: NgbTranslatedDatepicker },
    { provide: NgbTimepicker, useClass: NgbTimepicker },
    { provide: NgbDateParserFormatter, useClass: SbDateISOParserFormatter },
    { provide: NgbDateAdapter, useClass: SbDateStructAdapter },
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SbLocalDateTimeInputComponent), multi: true },
  ],
  standalone: false,
})
export class SbLocalDateTimeInputComponent implements OnInit, ControlValueAccessor, AfterViewInit {
  @Input() title: string;
  @Input() seconds: boolean = true;
  @Input() weekNumbers: boolean = true;
  @Input() disablePicker: boolean;
  @Input() kind: string = 'DEFAULT';
  @Input() inputRequired: boolean = false;

  @Output() modelChange: EventEmitter<SbLocalDateTime> = new EventEmitter<SbLocalDateTime>();

  @ViewChild('dateTimePicker') eRef: ElementRef;

  // tslint:disable-next-line:variable-name
  _minDate: SbLocalDateTime;
  resetDateTime: SbLocalDateTime;
  // tslint:disable-next-line:variable-name
  _dateTimeString: string;
  // tslint:disable-next-line:variable-name
  _dateStruct: NgbDateStruct;
  date: Date;
  visible: boolean;
  placeholder = this.translate.instant('lang.globals.selectDateAndTime');
  _startDate: SbLocalDateTime;
  _startDateStruct: NgbDateStruct;

  @Input('minDate')
  set minDate(val: SbLocalDateTime) {
    this._minDate = val;
    if (this._minDate && this._minDate.value) {
      this._minDateStruct = {
        year: this._minDate.value.year,
        month: this._minDate.value.month,
        day: this._minDate.value.day,
      };
    }
  }

  get minDate(): SbLocalDateTime {
    return this._minDate;
  }

  @Input('startDate')
  set startDate(val: SbLocalDateTime) {
    this._startDate = val;
    if (this._startDate && this._startDate.value) {
      this._startDateStruct = {
        year: this._startDate.value.year,
        month: this._startDate.value.month,
        day: this._startDate.value.day,
      };
    }
  }

  get startDate(): SbLocalDateTime {
    return this._startDate;
  }

  get startDateStruct(): NgbDateStruct {
    return this._startDateStruct;
  }

  // tslint:disable-next-line:variable-name
  _minDateStruct: NgbDateStruct;
  @Input('minDateStruct')
  set minDateStruct(val: NgbDateStruct) {
    this._minDateStruct = val;
  }

  get minDateStruct(): NgbDateStruct {
    return this._minDateStruct;
  }

  // tslint:disable-next-line:variable-name
  _maxDateStruct: NgbDateStruct;
  @Input('maxDateStruct')
  set maxDateStruct(val: NgbDateStruct) {
    this._maxDateStruct = val;
  }

  get maxDateStruct(): NgbDateStruct {
    return this._maxDateStruct;
  }

  // tslint:disable-next-line:variable-name
  _model: SbLocalDateTime;
  set model(value: SbLocalDateTime) {
    this._model = value;
    this.refreshFromSetter();
  }

  get model(): SbLocalDateTime {
    return this._model;
  }

  @Input('initialDate')
  set resetDate(value: SbLocalDateTime) {
    this.resetDateTime = value;
    this.model = value;
  }

  // tslint:disable-next-line:variable-name
  _timeStruct: NgbTimeStruct;
  set timeStruct(value: NgbTimeStruct) {
    this._timeStruct = value;
    this.refreshFromTimeStruct();
  }

  get timeStruct(): NgbTimeStruct {
    return this._timeStruct;
  }

  initTimeStruct: boolean = true;

  set dateTimeString(value: string) {
    this._dateTimeString = value;
  }

  get dateTimeString(): string {
    return this._dateTimeString;
  }

  set dateStruct(value: NgbDateStruct) {
    this._dateStruct = value;
    this.refreshFromDateStruct();
  }

  get dateStruct(): NgbDateStruct {
    return this._dateStruct;
  }

  constructor(
    public util: UtilService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {}

  @HostListener('document:click', ['$event'])
  clickOutside(e) {
    if (this.visible) {
      if (this.eRef.nativeElement.contains(e.target)) {
      } else {
        if (this._model) {
          this.sendDateTime();
        }
      }
    }
  }

  toggleDatepicker() {
    this.visible = !this.visible;
  }

  refreshFromDateStruct() {
    if (this.dateStruct) {
      const dt: SbLocalDateTime = new SbLocalDateTime();
      dt.value = DateTime.local();
      dt.value = dt.value.set({
        day: this.dateStruct.day,
        month: this.dateStruct.month,
        year: this.dateStruct.year,
        hour: this.kind !== 'RANGE_END' ? 0 : 23,
        minute: this.kind !== 'RANGE_END' ? 0 : 59,
        second: this.kind !== 'RANGE_END' ? 0 : 59,
        millisecond: this.kind !== 'RANGE_END' ? 0 : 999,
      });
      if (this.timeStruct) {
        dt.value = dt.value.set({
          hour: this.timeStruct.hour,
          minute: this.timeStruct.minute,
          second: this.timeStruct.second,
          millisecond: 0,
        });
      } else if (this.kind === 'RANGE_END') {
        this.timeStruct = {
          hour: 23,
          minute: 59,
          second: 59,
        };
      } else {
        this.timeStruct = {
          hour: 0,
          minute: 0,
          second: 0,
        };
      }
      if (this._minDate && this._minDate.value && !this.disablePicker) {
        if (dt.value < this._minDate.value) {
          // FALSCH
          this.refreshFromSetter();
        } else {
          // RICHTIG
          this._model = dt;
          this._dateTimeString = this.util.dateToDateTimeString(this.model.value, this.seconds);
        }
      } else {
        // RICHTIG
        this._model = dt;
        this._dateTimeString = this.util.dateToDateTimeString(this.model.value, this.seconds);
      }
    } else {
      this._model = null;
      this._dateTimeString = '';
    }

    this.reset();
    this.propagateChange(this.model);
  }

  refreshReset() {
    this.resetDateTime = this.model;
  }

  reset() {
    if (!(this.model && this.model.value && this.model.value.isValid)) {
      if (this.resetDateTime && this.resetDateTime.value && this.resetDateTime.value.isValid) {
        this.model = this.resetDateTime;
      } else {
        this.model = null;
      }
    }
  }

  clear() {
    this._dateStruct = null;
    this._timeStruct = null;
    this.model = null;
    this.modelChange.emit(this._model);
  }

  refreshFromDateString() {
    if (this.dateTimeString && this.util.parseDateFromDateString(this.dateTimeString)) {
      const dt: SbLocalDateTime = new SbLocalDateTime();
      dt.value = this.util.parseDateFromDateString(this.dateTimeString);
      if (this._minDate?.value && dt?.value && this._minDate.value.toMillis() > dt.value.toMillis()) {
        this.refreshFromDateStruct();
      } else {
        this._model = dt;
        this._dateStruct = {
          year: this.model.value.year,
          month: this.model.value.month,
          day: this.model.value.day,
        };
        this._timeStruct = {
          hour: this.model.value.hour,
          minute: this.model.value.minute,
          second: this.model.value.second,
        };
      }
      if (!this.resetDateTime) {
        this.refreshReset();
      }
    } else {
      this._model = null;
      this._dateStruct = null;
      this._timeStruct = null;
    }

    this.reset();

    this.sendDateTime();
    this.propagateChange(this.model);
  }

  refreshFromSetter() {
    if (this.model && this.model.value) {
      if (this._minDate && this._minDate.value && !this.disablePicker) {
        if (this.model.value < this._minDate.value) {
          // FALSCH
          this._model = new SbLocalDateTime(
            DateTime.local(
              this._minDate.value.year,
              this._minDate.value.month,
              this._minDate.value.day,
              this._minDate.value.hour,
              this._minDate.value.minute,
              this._minDate.value.second,
              this._minDate.value.millisecond
            )
          );
        } else {
          // RICHTIG
          this._model = this.model;
        }
      } else {
        // RICHTIG
        this._model = this.model;
      }
      this._dateStruct = {
        day: this.model.value.day,
        month: this.model.value.month,
        year: this.model.value.year,
      };
      this._timeStruct = {
        hour: this.model.value.hour,
        minute: this.model.value.minute,
        second: this.model.value.second,
      };
      this._dateTimeString = this.util.dateToDateTimeString(this.model.value, this.seconds);
      this.initTimeStruct = false;
      if (!this.resetDateTime) {
        this.refreshReset();
      }
    } else {
      if (this.initTimeStruct) {
        this._timeStruct = {
          hour: 0,
          minute: 0,
          second: 0,
        };
        this.initTimeStruct = false;
      }
      this._dateTimeString = this.placeholder;
    }
    // console.log(this.timeStruct);
    // console.log(this.model);
  }

  refreshFromTimeStruct() {
    this.initTimeStruct = false;
    if (this.model && this.model.value && this._timeStruct) {
      const dt: SbLocalDateTime = new SbLocalDateTime(this.model.value);
      dt.value = dt.value.set({
        hour: this._timeStruct.hour,
        minute: this._timeStruct.minute,
        second: this._timeStruct.second,
      });

      if (this.kind === 'RANGE_START') {
        this._timeStruct.second = this.seconds ? this._timeStruct.second : 0;
        dt.value = dt.value.set({
          millisecond: 0,
          second: this.seconds ? this._timeStruct.second : 0,
        });
      } else if (this.kind === 'RANGE_END') {
        this._timeStruct.second = this.seconds ? this._timeStruct.second : 59;
        dt.value = dt.value.set({
          millisecond: 999,
          second: this.seconds ? this._timeStruct.second : 59,
        });
      } else {
        this._timeStruct.second = this.seconds ? this._timeStruct.second : 0;
        dt.value = dt.value.set({
          millisecond: 0,
          second: this.seconds ? this._timeStruct.second : 0,
        });
      }

      this._model = dt;
      this._dateTimeString = this.util.dateToDateTimeString(this.model.value, this.seconds);
    }
    this.reset();
    this.propagateChange(this.model);
  }

  sendDateTime() {
    if (this.visible) {
      this.toggleDatepicker();
    }
    this.refreshReset();
    this.modelChange.emit(this.model);
  }

  cancel() {
    this.model = null;
    if (this.resetDateTime) {
      this.reset();
    }
    this.toggleDatepicker();
  }

  writeValue(obj: any): void {
    if (obj) {
      this.model = obj;
    }
  }

  propagateChange = (_: any) => {};

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}
}
